export const DEFAULT_LOCALE = "nb-NO";
export const DEFAULT_TZ = "Europe/Oslo";

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function lastDayOfMonth(year, month /* 0-based */) {
  // Day 0 of next month is the last day of current month
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function getYMDInTZ(date, timeZone = DEFAULT_TZ) {
  // Use a stable, locale-independent formatter to extract Y/M/D in a TZ
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
}

// ---------------------------------------------------------------------------
// Unified parsing
// ---------------------------------------------------------------------------
// parseDate attempts to normalise a wide variety of date/time inputs into a
// native Date instance or returns null when it cannot.
// Supported inputs:
//   - Date (validated)
//   - number (ms since epoch; if < 1e12 treated as seconds)
//   - object with toDate()
//   - ISO-8601 strings (YYYY-MM-DD, with optional time / timezone)
//   - Norwegian date string: DD.MM.YYYY
//   - Norwegian date-time string: DD.MM.YYYY[ kl.] HH:mm (seconds optional)
export function parseDate(value) {
  if (value == null || value === "") return null;

  // Fast-path Date
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value && typeof value.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }

  // Numeric epoch (seconds or milliseconds)
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = Math.abs(value) < 1e12 ? value * 1000 : value; // treat small numbers as seconds
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;

    // Norwegian date or date-time (allows optional 'kl.' and seconds)
    // Examples: 01.07.2025, 01.07.2025 14:30, 01.07.2025 kl. 14:30, 01.07.2025 kl. 14:30:59
    const norDateTimeRegex =
      /^(\d{2})\.(\d{2})\.(\d{4})(?:\s*(?:kl\.)?\s*(\d{2}):(\d{2})(?::(\d{2}))?)?$/i;
    const m = norDateTimeRegex.exec(trimmed);
    if (m) {
      const [, dd, mm, yyyy, HH, MM, SS] = m;
      const day = Number(dd);
      const monthIndex = Number(mm) - 1;
      const year = Number(yyyy);
      const hour = HH != null ? Number(HH) : 0;
      const minute = MM != null ? Number(MM) : 0;
      const second = SS != null ? Number(SS) : 0;
      // Basic range guards before constructing date
      if (
        monthIndex < 0 ||
        monthIndex > 11 ||
        day < 1 ||
        day > 31 ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
      ) {
        return null;
      }
      // For date-only we keep previous semantics (UTC midnight) to avoid DST drift in formatting.
      if (HH == null) {
        const d = new Date(Date.UTC(year, monthIndex, day));
        // Reject overflow (e.g. 32.01.2025 => spills into next month)
        if (
          Number.isNaN(d.getTime()) ||
          d.getUTCFullYear() !== year ||
          d.getUTCMonth() !== monthIndex ||
          d.getUTCDate() !== day
        ) {
          return null;
        }
        return d;
      }
      // For date-time create a local Date (consumer code formats with explicit TZ later).
      const d = new Date(year, monthIndex, day, hour, minute, second, 0);
      if (
        Number.isNaN(d.getTime()) ||
        d.getFullYear() !== year ||
        d.getMonth() !== monthIndex ||
        d.getDate() !== day ||
        d.getHours() !== hour ||
        d.getMinutes() !== minute ||
        d.getSeconds() !== second
      ) {
        return null;
      }
      return d;
    }

    // Fallback: attempt native Date parse (ISO 8601 and RFC2822 mostly)
    const iso = new Date(trimmed);
    return Number.isNaN(iso.getTime()) ? null : iso;
  }

  return null;
}

export function isValidDate(value) {
  return !!parseDate(value);
}

export function isDateOrNull(value) {
  return value == null || isValidDate(value);
}

export function toISOUTC(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function startOfDayUTC(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}

export function addDays(date, n) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getTime() + n * 86400000);
}

export function addMonths(date, n) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const targetMonthIndex = m + n;
  // Compute target year/month in UTC
  const targetYear = y + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12; // wrap correctly for negatives
  const lastDay = lastDayOfMonth(targetYear, targetMonth);
  const day = clamp(d, 1, lastDay);
  return new Date(
    Date.UTC(
      targetYear,
      targetMonth,
      day,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
}

export function compareAsc(a, b) {
  const at = a?.getTime?.();
  const bt = b?.getTime?.();
  if (Number.isNaN(at) || Number.isNaN(bt)) return NaN;
  if (at < bt) return -1;
  if (at > bt) return 1;
  return 0;
}

// Merge a date-only and time-only Date into a new Date (local time semantics)
// Falls back to the date's time when timePart is invalid.
export function mergeDateAndTime(datePart, timePart) {
  const base = parseDate(datePart);
  if (!base) return null;
  const t = parseDate(timePart);
  const hours = t ? t.getHours() : base.getHours();
  const minutes = t ? t.getMinutes() : base.getMinutes();
  const seconds = t ? t.getSeconds() : base.getSeconds();
  const ms = t ? t.getMilliseconds() : base.getMilliseconds();
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate(),
    hours,
    minutes,
    seconds,
    ms,
  );
}

export function isSameDay(a, b, timeZone = DEFAULT_TZ) {
  if (!(a instanceof Date) || Number.isNaN(a.getTime())) return false;
  if (!(b instanceof Date) || Number.isNaN(b.getTime())) return false;
  const da = getYMDInTZ(a, timeZone);
  const db = getYMDInTZ(b, timeZone);
  return da.year === db.year && da.month === db.month && da.day === db.day;
}

const formatters = {
  dateShort: (date, { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TZ } = {}) =>
    date.toLocaleDateString(locale, {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  dateLong: (date, { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TZ } = {}) =>
    date.toLocaleDateString(locale, {
      timeZone,
      day: "2-digit",
      month: "long",
      year: "numeric",
    }),
  dateTime: (date, { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TZ } = {}) =>
    date.toLocaleString(locale, {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: undefined,
      hour12: false,
    }),
  dateTimeWithSeconds: (
    date,
    { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TZ } = {},
  ) =>
    date.toLocaleString(locale, {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
  time: (date, { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TZ } = {}) =>
    date.toLocaleTimeString(locale, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      second: undefined,
      hour12: false,
    }),
};

export function formatDate(date, key, opts) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const f = formatters[key];
  if (!f) throw new Error(`Unknown date format key: ${key}`);
  return f(date, opts);
}

export function formatRelative(
  from,
  to = new Date(),
  { locale = DEFAULT_LOCALE } = {},
) {
  if (!(from instanceof Date) || Number.isNaN(from.getTime())) return "";
  if (!(to instanceof Date) || Number.isNaN(to.getTime())) return "";
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const diffMs = from.getTime() - to.getTime();

  const absMs = Math.abs(diffMs);
  const minutes = 60 * 1000;
  const hours = 60 * minutes;
  const days = 24 * hours;
  const weeks = 7 * days;
  const months = 30 * days; // approximation suitable for relative display
  const years = 365 * days;

  let value;
  /** @type {Intl.RelativeTimeFormatUnit} */
  let unit;
  if (absMs < minutes) {
    value = Math.round(diffMs / 1000);
    unit = "second";
  } else if (absMs < hours) {
    value = Math.round(diffMs / minutes);
    unit = "minute";
  } else if (absMs < days) {
    value = Math.round(diffMs / hours);
    unit = "hour";
  } else if (absMs < weeks) {
    value = Math.round(diffMs / days);
    unit = "day";
  } else if (absMs < months) {
    value = Math.round(diffMs / weeks);
    unit = "week";
  } else if (absMs < years) {
    value = Math.round(diffMs / months);
    unit = "month";
  } else {
    value = Math.round(diffMs / years);
    unit = "year";
  }

  return rtf.format(value, unit);
}

// -------------------------------------------------------------
// Migrated helpers from deprecated datetimeUtils (legacy surface)
// -------------------------------------------------------------
function pad(n) {
  return n.toString().padStart(2, "0");
}

function getOsloParts(date) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: DEFAULT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const map = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value]),
  );
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
  };
}

function formatPattern(date, pattern) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const { year, month, day, hour, minute } = getOsloParts(date);
  const tokens = {
    YYYY: year.toString(),
    DD: pad(day),
    MM: pad(month),
    HH: pad(hour),
    mm: pad(minute),
  };
  let out = pattern.replace(/\[(.+?)\]/g, (m, lit) => `__LIT_${lit}__`);
  Object.entries(tokens).forEach(([k, v]) => {
    out = out.replace(new RegExp(k, "g"), v);
  });
  out = out.replace(/__LIT_(.+?)__/g, (m, lit) => lit);
  return out;
}

export function toIsoString(datetime) {
  const d = parseDate(datetime);
  return d ? d.toISOString() : "";
}

export function toNorwegianDateFormat(datetime) {
  const d = parseDate(datetime);
  return formatPattern(d, "DD.MM.YYYY");
}

export function toNorwegianTimeFormat(datetime) {
  const d = parseDate(datetime);
  return formatPattern(d, "HH:mm");
}

export function toNorwegianDatetime(value) {
  const d = parseDate(value);
  if (!d) {
    return { format: () => "", valueOf: () => NaN, toDate: () => null };
  }
  return {
    format: (pattern) => formatPattern(d, pattern || "DD.MM.YYYY HH:mm"),
    valueOf: () => d.getTime(),
    toDate: () => new Date(d.getTime()),
  };
}

export function fromNorwegianDate(str) {
  if (typeof str !== "string" || !/^\d{2}\.\d{2}\.\d{4}$/.test(str))
    return null;
  const [dd, mm, yyyy] = str.split(".").map(Number);
  return new Date(Date.UTC(yyyy, mm - 1, dd));
}

export function mergeDateTime(date, time) {
  const d = parseDate(date);
  const t = parseDate(time);
  if (!d) return null;
  if (!t) return d;
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    t.getHours(),
    t.getMinutes(),
    t.getSeconds(),
    t.getMilliseconds(),
  );
}

export function getMostRecentObjectComparedOnParameter(array, parameterName) {
  let mostRecent;
  for (let i = 0; i < array.length; i++) {
    const element = array[i];
    if (i === 0) mostRecent = { ...element };
    if (
      Date.parse(element[parameterName]) > Date.parse(mostRecent[parameterName])
    ) {
      mostRecent = { ...element };
    }
  }
  return mostRecent;
}

export function StartDateIsLongerThanEndDate(fraDato, tilDato) {
  return Date.parse(fraDato) > Date.parse(tilDato);
}

export function datetimeIsDeactivated(isoString) {
  if (isoString == null || isoString === "") return false;
  if (!Date.parse(isoString)) return false;
  const currentIso = new Date().toISOString();
  return toNorwegianDatetime(isoString) < toNorwegianDatetime(currentIso);
}

export function formatDateTimeHavingDateTime(datetime) {
  if (!isValidDate(datetime)) return "";
  return formatPattern(parseDate(datetime), "DD.MM.YYYY [kl.] HH.mm");
}

export function formatDateHavingNorwegianDateTime(datetime) {
  if (!isValidDate(datetime)) return "";
  return formatPattern(parseDate(datetime), "DD.MM.YYYY");
}

export function formatTimeHavingNorwegianDateTime(datetime) {
  if (!isValidDate(datetime)) return "";
  return formatPattern(parseDate(datetime), "HH:mm");
}

export function formatDateTimeHavingNorwegianDateTime(datetime) {
  if (!isValidDate(datetime)) return "";
  return formatPattern(parseDate(datetime), "DD.MM.YYYY [kl.] HH:mm");
}

export function dateTimeFromTimePicker(tid) {
  const t = parseDate(tid);
  if (!t) return tid;
  const now = new Date();
  const merged = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    t.getHours(),
    t.getMinutes(),
    0,
    0,
  );
  return merged.toISOString();
}
